import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import authService from '../services/authService';
import apiClient from '../services/apiService';
import { toast } from 'react-toastify';
import { Lock, Eye, EyeOff } from 'lucide-react';

const Settings = () => {
  const user = authService.getUser();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState({
    new: false,
    confirm: false,
  });

  const resetPasswordMutation = useMutation({
    mutationFn: (password) => apiClient.post('/api/auth/reset-password', { password }),
    onSuccess: () => {
      setNewPassword('');
      setConfirmPassword('');
    },
  });

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleUpdatePassword = (e) => {
    e.preventDefault();

    if (!newPassword || !confirmPassword) {
      toast.error('Please fill in all password fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    // Submit reset password request
    toast.promise(
      resetPasswordMutation.mutateAsync(newPassword),
      {
        pending: {
          render: 'Updating password...',
        },
        success: {
          render: 'Password updated successfully!',
        },
        error: {
          render({ data }) {
            return data?.response?.data?.message || 'Failed to update password';
          },
        },
      }
    );
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-prose-brand">Settings</h1>
        <p className="text-xs sm:text-sm text-prose-black/60 mt-1">Manage your admin account settings</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Account Information - Sidebar */}
        <div className="space-y-4">
          {/* Admin Profile Card */}
          <div className="bg-white rounded-lg border border-gray-300 p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-bold text-prose-brand mb-4">Your Account</h3>

            <div className="space-y-4">
              <div>
                <p className="text-prose-black/60 text-xs sm:text-sm font-medium">Email Address</p>
                <p className="text-prose-black font-semibold mt-1 text-sm break-all">{user?.email || 'N/A'}</p>
              </div>

              <div>
                <p className="text-prose-black/60 text-xs sm:text-sm font-medium">Name</p>
                <p className="text-prose-black font-semibold mt-1 text-sm">{user?.name || 'N/A'}</p>
              </div>

              <div className="pt-4 border-t border-gray-300">
                <p className="text-prose-black/60 text-xs sm:text-sm font-medium">Member Since</p>
                <p className="text-prose-black text-xs sm:text-sm mt-1">
                  {user?.created_at
                    ? new Date(user.created_at).toLocaleDateString()
                    : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Password Change Form - Main Content */}
        <div className="lg:col-span-2">
          <form onSubmit={handleUpdatePassword} className="bg-white rounded-lg border border-gray-300 p-4 sm:p-6">
            <h3 className="text-lg sm:text-xl font-bold text-prose-brand mb-4 sm:mb-6">Change Password</h3>

            <div className="space-y-4 sm:space-y-6">

              {/* New Password */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-prose-black mb-2">
                  New Password *
                </label>
                <div className="relative mb-3">
                  <Lock className="absolute left-3 top-2.5 sm:top-3 text-prose-brand/60 flex-shrink-0" size={18} />
                  <input
                    type={showPasswords.new ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    className="w-full pl-9 sm:pl-10 pr-10 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-prose-brand text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('new')}
                    className="absolute right-3 top-2.5 sm:top-3 text-prose-brand/60 hover:text-prose-brand flex-shrink-0"
                  >
                    {showPasswords.new ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-prose-black mb-2">
                  Confirm New Password *
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-2.5 sm:top-3 text-prose-brand/60 flex-shrink-0" size={18} />
                  <input
                    type={showPasswords.confirm ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    className="w-full pl-9 sm:pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-prose-brand text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('confirm')}
                    className="absolute right-3 top-2.5 sm:top-3 text-prose-brand/60 hover:text-prose-brand flex-shrink-0"
                  >
                    {showPasswords.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>

                {/* Match Status */}
                {confirmPassword && (
                  <p
                    className={`text-xs sm:text-sm mt-2 ${
                      newPassword === confirmPassword ? 'text-green-600' : 'text-red-600'
                    }`}
                  >
                    {newPassword === confirmPassword ? '✓ Passwords match' : '✗ Passwords do not match'}
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <div className="pt-4 sm:pt-6 border-t border-gray-300">
                <button
                  type="submit"
                  disabled={resetPasswordMutation.isPending || newPassword !== confirmPassword}
                  className="w-full px-4 sm:px-6 py-2 bg-prose-brand text-white rounded-lg hover:bg-prose-brand/90 transition duration-200 font-semibold disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                >
                  {resetPasswordMutation.isPending ? 'Updating Password...' : 'Update Password'}
                </button>
              </div>

            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Settings;
